import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    Box, Typography, Button, Container, Card, CardContent, 
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Fab, Tooltip, FormControlLabel, Radio, RadioGroup, FormLabel,
    Menu, MenuItem, Avatar, AvatarGroup, CircularProgress
} from '@mui/material';
import { 
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, 
    Share as ShareIcon, Info as InfoIcon, Save as SaveIcon,
    CloudDone as CloudDoneIcon, Sync as SyncIcon, SyncProblem as SyncProblemIcon,
    DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import StateContext from '../../Contexts/StateContext';
import Axios from '../../AxiosInstance';
import config, { environment } from '../../config';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Simple debounce implementation since lodash might not be available
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

const CARD_WIDTH = 200;
const CARD_FIXED_Y_OFFSET = 50; // Fix eltolás a vonalak csatlakozásához a kártya tetejétől

export default function FamilyTreeEditor() {
    const { t, i18n } = useTranslation();
    const globalState = React.useContext(StateContext);
    const { id, token } = useParams();
    const [members, setMembers] = useState([]);
    const [connections, setConnections] = useState([]);
    const [treeTitle, setTreeTitle] = useState('');
    const [activeEditors, setActiveEditors] = useState([]);
    const [sessionId] = useState(() => Math.random().toString(36).substring(7));
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        birthYear: '',
        birthPlace: '',
        maidenName: '',
        partnerName: '',
        gender: 'man',
        x: 100,
        y: 100
    });
    
    const [draggedMember, setDraggedMember] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [connectingFrom, setConnectingFrom] = useState(null);
    const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
    
    const [openSaveAsDialog, setOpenSaveAsDialog] = useState(false);
    const [saveAsTitle, setSaveAsTitle] = useState('');
    
    const [isResourceFound, setIsResourceFound] = useState(true);
    const [lastSaveTarget, setLastSaveTarget] = useState(null); // 'db' or 'local'
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
    const containerRef = useRef(null);
    const exportRef = useRef(null);

    const debouncedSaveToDatabase = useCallback(
        debounce(async (data, currentToken) => {
            setSaveStatus('saving');
            try {
                if (currentToken && currentToken !== 'secret') {
                    await Axios.put(`${config[environment].familyTreeEndpoint}${currentToken}/`, data);
                } else {
                    const response = await Axios.post(config[environment].familyTreeEndpoint, data);
                    if (response.data && response.data.token) {
                        window.history.replaceState(null, '', `/${i18n.language}/csaladfaallitas/${response.data.id || response.data.tree_id}/${response.data.token}`);
                        // Update token for future saves
                        // We need a way to update the token state if it changes, but here it's easier to reload or just use the new token
                    }
                }
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 3000);
            } catch (error) {
                console.error("Auto-save failed", error);
                setSaveStatus('error');
            }
        }, 2000),
        [i18n.language]
    );

    const triggerAutoSave = (newMembers, newConnections, newTitle = treeTitle) => {
        if (!token || token === 'secret') return;
        if (saveStatus === 'saving') return; // Don't trigger if already saving

        const data = {
            title: newTitle,
            members: newMembers.map(m => ({
                id: m.id,
                name: m.name,
                birth_place: m.birthPlace,
                birth_year: m.birthYear,
                maiden_name: m.maidenName,
                partner_name: m.partnerName,
                gender: m.gender,
                x: m.x,
                y: m.y
            })),
            connections: newConnections.map(c => ({
                from_member_id: c.from,
                to_member_id: c.to
            }))
        };
        debouncedSaveToDatabase(data, token);
    };


    const handleMouseDown = (member, e) => {
        // Only drag if not clicking on an action button
        if (e.target.closest('button') || e.target.closest('.MuiIconButton-root')) {
            return;
        }
        
        if (connectingFrom) {
            if (connectingFrom.id !== member.id) {
                // Check if connection already exists
                const exists = connections.some(c => 
                    (c.from === connectingFrom.id && c.to === member.id) ||
                    (c.from === member.id && c.to === connectingFrom.id)
                );
                
                if (!exists) {
                    const newConns = [...connections, { from: connectingFrom.id, to: member.id }];
                    setConnections(newConns);
                    saveToLocal(members, newConns);
                    triggerAutoSave(members, newConns);
                }
                setConnectingFrom(null);
            } else {
                setConnectingFrom(null);
            }
            return;
        }

        setDraggedMember(member);
        setDragOffset({
            x: e.clientX - member.x,
            y: e.clientY - member.y
        });
    };

    const handleMouseMove = (e) => {
        if (draggedMember) {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            const newMembers = members.map(m => 
                m.id === draggedMember.id ? { ...m, x: newX, y: newY, isNew: false } : m
            );
            setMembers(newMembers);
        }
    };

    const handleMouseUp = () => {
        if (draggedMember) {
            saveToLocal(members, connections);
            triggerAutoSave(members, connections);
            setDraggedMember(null);
        }
    };

    // Initial load from DB or Local
    useEffect(() => {
        const fetchData = async () => {
            // First try DB if we have a token (secret link)
            if (token && token !== 'secret') {
                try {
                    const response = await Axios.get(`${config[environment].familyTreeEndpoint}${token}/`).catch(error => {
                        if (error.response?.status === 404) {
                            throw error;
                        }
                        throw error;
                    });
                    if (response.data) {
                        const mappedMembers = (response.data.members || []).map(m => ({
                            id: m.id,
                            name: m.name,
                            birthPlace: m.birth_place,
                            birthYear: m.birth_year,
                            maidenName: m.maiden_name,
                            partnerName: m.partner_name,
                            gender: m.gender,
                            x: m.x,
                            y: m.y
                        }));
                        const mappedConnections = (response.data.connections || []).map(c => ({
                            from: c.from_member_id,
                            to: c.to_member_id
                        }));
                        setMembers(mappedMembers);
                        setConnections(mappedConnections);
                        setTreeTitle(response.data.title || '');
                        setActiveEditors(response.data.active_editors || []);
                        setIsResourceFound(true);
                        
                        // Update local cache with DB data to keep them in sync
                        localStorage.setItem(`family_tree_${id}`, JSON.stringify({
                            members: mappedMembers,
                            connections: mappedConnections,
                            title: response.data.title || '',
                            token: token
                        }));
                        return; // Successfully loaded from DB
                    }
                } catch (error) {
                    if (error.response?.status !== 404) {
                        console.error('Fetch from DB failed', error);
                    } else {
                        setIsResourceFound(false);
                    }
                }
            }

            // Fallback to localStorage ONLY if logged in
            if (globalState.userIsLogged) {
                const savedMembers = localStorage.getItem(`family_tree_${id}`);
                if (savedMembers) {
                    const data = JSON.parse(savedMembers);
                    setMembers(data.members || []);
                    setConnections(data.connections || []);
                    setTreeTitle(data.title || '');
                } else {
                    // Add a default member if the tree is new/empty
                    const initialMember = {
                        id: Date.now(),
                        name: t('Thematic.FamilyTree.Name'),
                        birthYear: '',
                        birthPlace: '',
                        maidenName: '',
                        partnerName: '',
                        gender: 'man',
                        x: window.innerWidth / 2 - 100,
                        y: window.innerHeight / 2 - 100
                    };
                    setMembers([initialMember]);
                    saveToLocal([initialMember], []);
                }
            } else if (token === 'secret') {
                // Not logged in and no token - don't show anything or show empty
                setIsResourceFound(false);
            }
        };

        fetchData();
    }, [id, token, t, globalState.userIsLogged]);

    // Heartbeat to track active editors and sync data
    useEffect(() => {
        if (!token || token === 'secret' || !isResourceFound) {
            return;
        }

        const sendHeartbeat = async (isLeaving = false) => {
            // Check again inside the function to be safe
            if (!isResourceFound && !isLeaving) {
                return;
            }

            try {
                const payload = {
                    session_id: sessionId,
                    user_name: globalState.userUsername || t('Thematic.FamilyTree.Guest'),
                    is_leaving: isLeaving
                };

                const url = `${config[environment].familyTreeEndpoint}${token}/heartbeat/`;

                if (isLeaving) {
                    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
                    if (navigator.sendBeacon) {
                        navigator.sendBeacon(url, blob);
                    } else {
                        Axios.post(url, payload).catch(err => {}); // Silent catch for leaving
                    }
                    return;
                }

                const response = await Axios.post(url, payload).catch(error => {
                    // Csendesen továbbdobjuk a 404-et a külső catch-nek
                    if (error.response?.status === 404) {
                        throw error;
                    }
                    throw error;
                });
                
                if (response.data && !isLeaving) {
                    setActiveEditors(response.data.active_editors || []);
                    
                    // If there are other editors, we might want to sync data
                    // For now, let's just sync if the data is different and we are not currently dragging
                    if (response.data.tree && !draggedMember && saveStatus === 'idle') {
                        const newTree = response.data.tree;
                        // Simple check to avoid unnecessary updates
                        if (newTree.members?.length !== members.length || newTree.title !== treeTitle) {
                             const mappedMembers = (newTree.members || []).map(m => ({
                                id: m.id,
                                name: m.name,
                                birthPlace: m.birth_place,
                                birthYear: m.birth_year,
                                maidenName: m.maiden_name,
                                partnerName: m.partner_name,
                                gender: m.gender,
                                x: m.x,
                                y: m.y
                            }));
                            const mappedConnections = (newTree.connections || []).map(c => ({
                                from: c.from_member_id,
                                to: c.to_member_id
                            }));
                            
                            setMembers(mappedMembers);
                            setConnections(mappedConnections);
                            setTreeTitle(newTree.title || '');
                        }
                    }
                }
            } catch (error) {
                // If it's a 401, AxiosInstance will handle token refresh or redirect.
                // If it's a 404, the resource might have been deleted, stop heartbeats.
                if (error.response?.status !== 401 && error.response?.status !== 404) {
                    console.error('Heartbeat failed', error);
                } else if (error.response?.status === 401) {
                    // Only remove token if user was logged in
                    if (globalState.userIsLogged) {
                        localStorage.removeItem('jwtAccessToken');
                    }
                } else if (error.response?.status === 404) {
                    setIsResourceFound(false);
                }
            }
        };

        // Initial heartbeat
        sendHeartbeat();

        // Interval heartbeat every 10 seconds for better collaboration
        const interval = setInterval(() => {
            if (isResourceFound) {
                sendHeartbeat(false);
            } else {
                clearInterval(interval);
            }
        }, 10000);

        // Handle page unload/navigation away
        const handleUnload = () => {
            sendHeartbeat(true);
        };

        window.addEventListener('beforeunload', handleUnload);

        return () => {
            clearInterval(interval);
            window.removeEventListener('beforeunload', handleUnload);
            handleUnload();
        };
    }, [token, sessionId, globalState.userUsername, globalState.userIsLogged, isResourceFound, members.length, treeTitle, draggedMember, saveStatus, t]);

    const saveToLocal = (newMembers, newConnections, newTitle = treeTitle) => {
        setLastSaveTarget('local');
        localStorage.setItem(`family_tree_${id}`, JSON.stringify({
            members: newMembers,
            connections: newConnections,
            title: newTitle,
            token: token // Elmentjük a tokent is a későbbi visszakereshetőséghez
        }));
    };

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setTreeTitle(newTitle);
        saveToLocal(members, connections, newTitle);
        triggerAutoSave(members, connections, newTitle);
    };

    const handleAddMember = (e) => {
        const rect = containerRef.current.getBoundingClientRect();
        setEditingMember(null);
        setFormData({
            name: '',
            birthYear: '',
            birthPlace: '',
            maidenName: '',
            partnerName: '',
            gender: 'man',
            x: window.innerWidth / 2 - 100,
            y: window.innerHeight / 2 - 100
        });
        setOpenDialog(true);
    };

    const handleEditMember = (member) => {
        setEditingMember(member);
        setFormData(member);
        setOpenDialog(true);
    };

    const handleDeleteMember = (memberId) => {
        const newMembers = members.filter(m => m.id !== memberId);
        const newConnections = connections.filter(c => c.from !== memberId && c.to !== memberId);
        setMembers(newMembers);
        setConnections(newConnections);
        saveToLocal(newMembers, newConnections);
        triggerAutoSave(newMembers, newConnections);
    };

    const handleDeleteConnection = (idx) => {
        if (window.confirm(t('Thematic.FamilyTree.DeleteConnectionConfirm'))) {
            const newConnections = connections.filter((_, i) => i !== idx);
            setConnections(newConnections);
            saveToLocal(members, newConnections);
            triggerAutoSave(members, newConnections);
        }
    };

    const handleSaveMember = () => {
        let newMembers;
        if (editingMember) {
            newMembers = members.map(m => m.id === editingMember.id ? { ...formData, id: m.id } : m);
        } else {
            const newId = Date.now();
            newMembers = [...members, { ...formData, id: newId, isNew: true }];
        }
        setMembers(newMembers);
        saveToLocal(newMembers, connections);
        triggerAutoSave(newMembers, connections);
        setOpenDialog(false);
    };

    const handleShare = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url);
        alert(t('Thematic.FamilyTree.ShareLink') + ": " + url);
    };

    const clearLocalCache = () => {
        if (window.confirm(t('Thematic.FamilyTree.ClearLocalCacheConfirm'))) {
            localStorage.removeItem(`family_tree_${id}`);
            alert(t('Thematic.FamilyTree.LocalCacheCleared'));
            window.location.reload();
        }
    };

    const handleSaveMenuOpen = (event) => {
        setSaveMenuAnchor(event.currentTarget);
    };

    const handleSaveMenuClose = () => {
        setSaveMenuAnchor(null);
    };

    const handleSaveToDatabase = async (forceNew = false) => {
        handleSaveMenuClose();
        if (forceNew) {
            setSaveAsTitle(`${treeTitle} (copy)`);
            setOpenSaveAsDialog(true);
            return;
        }
        await performSaveToDatabase(false);
    };

    const performSaveToDatabase = async (forceNew = false, customTitle = null) => {
        try {
            const finalTitle = customTitle || treeTitle;
            const data = {
                title: finalTitle,
                members: members.map(m => ({
                    id: m.id,
                    name: m.name,
                    birth_place: m.birthPlace,
                    birth_year: m.birthYear,
                    maiden_name: m.maidenName,
                    partner_name: m.partnerName,
                    gender: m.gender,
                    x: m.x,
                    y: m.y
                })),
                connections: connections.map(c => ({
                    from_member_id: c.from,
                    to_member_id: c.to
                }))
            };
            
            let response;
            if (token && token !== 'secret' && !forceNew) {
                try {
                    response = await Axios.put(`${config[environment].familyTreeEndpoint}${token}/`, data);
                } catch (error) {
                    // Csendesen kezeljük a 404-et, mert a catch ágban úgyis lefut a logika
                    if (error.response?.status === 404) {
                        throw error; // Továbbdobjuk a külső catch-nek
                    }
                    throw error;
                }
            } else {
                response = await Axios.post(config[environment].familyTreeEndpoint, data);
            }
            
            // Frissítsük az állapotot a backendtől kapott adatokkal (ID-k szinkronizálása)
            if (response.data && response.data.token) {
                const mappedMembers = (response.data.members || []).map(m => ({
                    id: m.id,
                    name: m.name,
                    birthPlace: m.birth_place,
                    birthYear: m.birth_year,
                    maidenName: m.maiden_name,
                    partnerName: m.partner_name,
                    gender: m.gender,
                    x: m.x,
                    y: m.y
                }));
                const mappedConnections = (response.data.connections || []).map(c => ({
                    from: c.from_member_id,
                    to: c.to_member_id
                }));
                
                if (!forceNew) {
                    setMembers(mappedMembers);
                    setConnections(mappedConnections);
                    if (customTitle) setTreeTitle(customTitle);
                    saveToLocal(mappedMembers, mappedConnections, response.data.title || finalTitle, response.data.token);
                } else {
                    // For "Save As New", we don't want to update the current local storage with the new name
                    // The reload/navigation below will take care of loading the new tree
                    // We can optionally save to the NEW local storage key if we want
                    localStorage.setItem(`family_tree_${response.data.id || response.data.tree_id}`, JSON.stringify({
                        members: mappedMembers,
                        connections: mappedConnections,
                        title: response.data.title || finalTitle,
                        token: response.data.token
                    }));
                }
                
                // Update URL if it's new or changed
                if (!window.location.href.includes(response.data.token)) {
                    if (forceNew) {
                        // For Save As New, navigate to the new tree
                        window.location.href = `/${i18n.language}/csaladfaallitas/${response.data.id || response.data.tree_id}/${response.data.token}`;
                        return; // Stop execution as we are navigating away
                    } else {
                        window.history.replaceState(null, '', `/${i18n.language}/csaladfaallitas/${response.data.id || response.data.tree_id}/${response.data.token}`);
                    }
                }
                if (!forceNew) setIsResourceFound(true);
            }
            
            alert(t('Thematic.FamilyTree.SaveSuccess'));
            setLastSaveTarget('db');
            setOpenSaveAsDialog(false);
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Save to DB failed', error);
            }
            // Fallback to local save if DB fails, but notify user
            saveToLocal(members, connections, customTitle || treeTitle);
            if (error.response?.status === 404) {
                setIsResourceFound(false);
                alert(t('Thematic.FamilyTree.NotFound'));
            } else {
                alert(t('Thematic.FamilyTree.SaveError') + " (Local save performed)");
            }
        }
    };

    const handleSaveToPDF = async () => {
        handleSaveMenuClose();
        if (!exportRef.current) return;

        try {
            // Find the bounds of the tree to capture it correctly
            if (members.length === 0) return;
            
            let minX = Math.min(...members.map(m => m.x));
            let minY = Math.min(...members.map(m => m.y));
            let maxX = Math.max(...members.map(m => m.x)) + CARD_WIDTH;
            let maxY = Math.max(...members.map(m => m.y)) + 200; // Tartalék a dinamikus magassághoz

            // Add some padding
            const padding = 80; // Increased padding to fit title at the top
            const width = maxX - minX + padding * 2;
            const height = maxY - minY + padding * 2;

            const canvas = await html2canvas(exportRef.current, {
                useCORS: true,
                scale: 2, // Higher quality
                backgroundColor: '#f5f5f5',
                x: minX - padding,
                y: minY - padding,
                width: width,
                height: height,
                onclone: (clonedDoc) => {
                    // Add title to the cloned document before rendering
                    const treeTitleElement = clonedDoc.createElement('div');
                    treeTitleElement.innerText = treeTitle || t('Thematic.FamilyTree.Title');
                    treeTitleElement.style.position = 'absolute';
                    // Position at the very top of the captured area
                    treeTitleElement.style.top = `${minY - padding + 20}px`; 
                    treeTitleElement.style.left = `${minX - padding}px`;
                    treeTitleElement.style.fontSize = '32px';
                    treeTitleElement.style.fontWeight = 'bold';
                    treeTitleElement.style.color = '#1976d2';
                    treeTitleElement.style.fontFamily = 'Arial, sans-serif';
                    treeTitleElement.style.width = `${width}px`;
                    treeTitleElement.style.textAlign = 'center';
                    treeTitleElement.style.zIndex = '1000';
                    
                    // Find the container in the cloned document
                    const clonedExportBox = clonedDoc.getElementById('family-tree-export-container');
                    if (clonedExportBox) {
                        clonedExportBox.appendChild(treeTitleElement);
                    }
                }
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: width > height ? 'l' : 'p',
                unit: 'px',
                format: [width, height]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, width, height);
            pdf.save(`${treeTitle || 'csaladfa'}.pdf`);
        } catch (error) {
            console.error('PDF export failed', error);
            alert(t('Thematic.FamilyTree.SaveError'));
        }
    };

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', position: 'relative', pt: { xs: '20px', md: '50px' } }}>
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 10, flexWrap: 'nowrap', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: 2 }}>
                    <Typography variant="h6" sx={{ mr: 1, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>{t('Thematic.FamilyTree.Title')}</Typography>
                    {lastSaveTarget && (
                        <Tooltip title={lastSaveTarget === 'db' ? t('Thematic.FamilyTree.SavedToDB') : t('Thematic.FamilyTree.SavedLocally')}>
                            <Box sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                bgcolor: lastSaveTarget === 'db' ? 'success.main' : 'warning.main',
                                mr: 1
                            }} />
                        </Tooltip>
                    )}
                    <Tooltip title={<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{t('Thematic.FamilyTree.InfoTooltip')}</Typography>} arrow>
                        <IconButton size="small" sx={{ color: '#ff9800' }}>
                            <InfoIcon sx={{ fontSize: { xs: 25, md: 35 } }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: { xs: 1, md: 2 }, minWidth: 0, alignItems: 'center' }}>
                    <TextField 
                        fullWidth 
                        variant="outlined" 
                        size="small" 
                        placeholder={t('Thematic.FamilyTree.TitlePlaceholder')}
                        value={treeTitle}
                        onChange={handleTitleChange}
                        sx={{ maxWidth: '400px', mr: 2 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        {saveStatus === 'saving' && <CircularProgress size={20} color="primary" />}
                        {saveStatus === 'saved' && <CloudDoneIcon color="success" sx={{ fontSize: 20 }} />}
                        {saveStatus === 'error' && <SyncProblemIcon color="error" sx={{ fontSize: 20 }} />}
                    </Box>
                    {activeEditors.length > 0 && (
                        <AvatarGroup max={4} size="small" sx={{ 
                            '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem' },
                            display: { xs: 'none', sm: 'flex' }
                        }}>
                            {activeEditors.map((editor, idx) => (
                                <Tooltip key={idx} title={editor.user_name}>
                                    <Avatar alt={editor.user_name}>
                                        {editor.user_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                </Tooltip>
                            ))}
                        </AvatarGroup>
                    )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <Button startIcon={<ShareIcon />} onClick={handleShare} sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}>{t('Thematic.FamilyTree.ShareLink')}</Button>
                    <IconButton onClick={handleShare} sx={{ mr: 1, display: { xs: 'inline-flex', md: 'none' } }} color="primary"><ShareIcon /></IconButton>
                    
                    <Button 
                        variant="outlined" 
                        startIcon={<SaveIcon />} 
                        onClick={handleSaveMenuOpen} 
                        sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
                    >
                        {t('Thematic.FamilyTree.Save')}
                    </Button>
                    <IconButton variant="outlined" onClick={handleSaveMenuOpen} sx={{ mr: 1, display: { xs: 'inline-flex', md: 'none' } }} color="primary"><SaveIcon /></IconButton>
                    
                    <Button 
                        variant="outlined" 
                        startIcon={<DeleteSweepIcon />} 
                        onClick={clearLocalCache} 
                        sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
                        color="error"
                    >
                        {t('Thematic.FamilyTree.ClearLocalCache')}
                    </Button>
                    <IconButton variant="outlined" onClick={clearLocalCache} sx={{ mr: 1, display: { xs: 'inline-flex', md: 'none' } }} color="error"><DeleteSweepIcon /></IconButton>
                    
                    <Menu
                        anchorEl={saveMenuAnchor}
                        open={Boolean(saveMenuAnchor)}
                        onClose={handleSaveMenuClose}
                    >
                        <MenuItem onClick={() => handleSaveToDatabase(false)}>{t('Thematic.FamilyTree.SaveToDatabase')}</MenuItem>
                        {globalState.userIsLogged && (
                            <MenuItem onClick={() => handleSaveToDatabase(true)}>{t('Thematic.FamilyTree.SaveAsNew')}</MenuItem>
                        )}
                        <MenuItem onClick={handleSaveToPDF}>{t('Thematic.FamilyTree.SaveToPDF')}</MenuItem>
                    </Menu>
                    
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMember} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>{t('Thematic.FamilyTree.AddMember')}</Button>
                    <IconButton variant="contained" onClick={handleAddMember} sx={{ display: { xs: 'inline-flex', md: 'none' }, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}><AddIcon /></IconButton>
                </Box>
            </Box>

            {!isResourceFound && (
                <Box sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography>{t('Thematic.FamilyTree.NotFound')}</Typography>
                </Box>
            )}


            <Box 
                ref={containerRef}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                sx={{ 
                    flexGrow: 1, 
                    position: 'relative', 
                    overflow: 'auto', 
                    bgcolor: '#f5f5f5',
                    backgroundImage: 'radial-gradient(#d1d1d1 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    minHeight: '500px',
                    '& svg': {
                        zIndex: 0
                    },
                    '& .MuiCard-root': {
                        zIndex: 10
                    },
                    '@keyframes pulse-highlight': {
                        '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0px rgba(25, 118, 210, 0.7)' },
                        '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 20px rgba(25, 118, 210, 0)' },
                        '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0px rgba(25, 118, 210, 0)' }
                    }
                }}
            >
                <Box id="family-tree-export-container" ref={exportRef} sx={{ position: 'absolute', top: 0, left: 0, width: '5000px', height: '5000px', pointerEvents: 'none' }}>
                    {/* SVG for connections inside export box */}
                    <svg style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%', 
                        zIndex: 5,
                        pointerEvents: 'none' 
                    }}>
                        {connections.map((conn, idx) => {
                            const from = members.find(m => m.id === conn.from);
                            const to = members.find(m => m.id === conn.to);
                            if (!from || !to) return null;
                            
                            const x1 = from.x + CARD_WIDTH / 2;
                            const y1 = from.y + CARD_FIXED_Y_OFFSET;
                            const x2 = to.x + CARD_WIDTH / 2;
                            const y2 = to.y + CARD_FIXED_Y_OFFSET;

                            return (
                                <g 
                                    key={idx} 
                                    style={{ cursor: 'pointer', pointerEvents: 'auto' }} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteConnection(idx);
                                    }}
                                >
                                    {/* Invisible thicker line for easier clicking */}
                                    <line 
                                        x1={x1} y1={y1} x2={x2} y2={y2} 
                                        stroke="transparent" 
                                        strokeWidth="15" 
                                    />
                                    <line 
                                        x1={x1} y1={y1} x2={x2} y2={y2} 
                                        stroke="#999" 
                                        strokeWidth="2"
                                        style={{ transition: 'stroke 0.2s' }}
                                        onMouseOver={(e) => e.target.style.stroke = 'red'}
                                        onMouseOut={(e) => e.target.style.stroke = '#999'}
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {members.map(member => (
                        <Card 
                            key={member.id}
                            onMouseDown={(e) => handleMouseDown(member, e)}
                            sx={{ 
                                width: CARD_WIDTH, 
                                minHeight: 80,
                                height: 'auto',
                                position: 'absolute', 
                                left: member.x, 
                                top: member.y,
                                pointerEvents: 'auto',
                                cursor: connectingFrom ? 'crosshair' : (draggedMember?.id === member.id ? 'grabbing' : 'grab'),
                                boxShadow: 3,
                                borderLeft: 5,
                                borderColor: member.gender === 'woman' ? 'pink' : 'blue',
                                backgroundColor: member.isNew ? '#fff9c4' : 'background.paper',
                                outline: connectingFrom?.id === member.id ? '3px solid #1976d2' : 'none',
                                outlineOffset: '2px',
                                userSelect: 'none',
                                zIndex: draggedMember?.id === member.id ? 100 : 10,
                                transition: 'all 0.3s ease-in-out',
                                overflow: 'visible',
                                '& .actions': { 
                                    maxHeight: 0,
                                    opacity: 0, 
                                    transition: 'all 0.3s ease-in-out',
                                    pointerEvents: 'none',
                                    overflow: 'hidden'
                                },
                                '&:hover': {
                                    zIndex: 110,
                                    transform: 'translateY(-5px)',
                                    boxShadow: 6
                                },
                                '&:hover .actions': { 
                                    maxHeight: '100px',
                                    opacity: 1,
                                    pointerEvents: 'auto',
                                    mt: 1
                                }
                            }}
                        >
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle1" noWrap fontWeight="bold">{member.name}</Typography>
                                {member.birthPlace && (
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: -0.5, fontStyle: 'italic' }}>
                                        ({member.birthPlace})
                                    </Typography>
                                )}
                                
                                <Box className="details" sx={{ mt: 1 }}>
                                    {member.birthYear && (
                                        <Typography variant="body2">{t('Thematic.FamilyTree.BirthYear')}: {member.birthYear}</Typography>
                                    )}
                                    {member.gender === 'woman' && member.maidenName && (
                                        <Typography variant="caption" display="block">
                                            {t('Thematic.FamilyTree.MaidenName')}: {member.maidenName}
                                        </Typography>
                                    )}
                                    {member.partnerName && (
                                        <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', mb: 0.5 }}>
                                            {member.gender === 'man' ? t('Thematic.FamilyTree.Wife') : t('Thematic.FamilyTree.Husband')}: {member.partnerName}
                                        </Typography>
                                    )}
                                </Box>

                                <Box className="actions" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <IconButton size="small" onClick={() => handleEditMember(member)}><EditIcon fontSize="inherit" /></IconButton>
                                    <IconButton size="small" onClick={() => handleDeleteMember(member.id)}><DeleteIcon fontSize="inherit" /></IconButton>
                                    <Tooltip title={t('Thematic.FamilyTree.Connect')}>
                                        <IconButton 
                                            size="small" 
                                            color={connectingFrom?.id === member.id ? "primary" : "default"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConnectingFrom(member);
                                            }}
                                        >
                                            <AddIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>{editingMember ? t('Thematic.FamilyTree.EditMember') : t('Thematic.FamilyTree.AddMember')}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={t('Thematic.FamilyTree.Name')}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label={t('Thematic.FamilyTree.BirthYear')}
                        value={formData.birthYear}
                        onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label={t('Thematic.FamilyTree.BirthPlace')}
                        value={formData.birthPlace}
                        onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
                        margin="dense"
                    />
                    <FormLabel component="legend" sx={{ mt: 2 }}>{t('Common.Type')}</FormLabel>
                    <RadioGroup
                        row
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    >
                        <FormControlLabel value="man" control={<Radio />} label={t('Thematic.FamilyTree.Man')} />
                        <FormControlLabel value="woman" control={<Radio />} label={t('Thematic.FamilyTree.Woman')} />
                    </RadioGroup>
                    {formData.gender === 'woman' && (
                        <TextField
                            fullWidth
                            label={t('Thematic.FamilyTree.MaidenName')}
                            value={formData.maidenName}
                            onChange={(e) => setFormData({ ...formData, maidenName: e.target.value })}
                            margin="dense"
                        />
                    )}
                    <TextField
                        fullWidth
                        label={formData.gender === 'man' ? t('Thematic.FamilyTree.Wife') : t('Thematic.FamilyTree.Husband')}
                        value={formData.partnerName}
                        onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
                        margin="dense"
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <TextField
                            label={t('Map.Latitude')}
                            type="number"
                            value={formData.x}
                            onChange={(e) => setFormData({ ...formData, x: parseInt(e.target.value) })}
                        />
                        <TextField
                            label={t('Map.Longitude')}
                            type="number"
                            value={formData.y}
                            onChange={(e) => setFormData({ ...formData, y: parseInt(e.target.value) })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t('Common.Cancel')}</Button>
                    <Button onClick={handleSaveMember} variant="contained">{t('Thematic.FamilyTree.Save')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openSaveAsDialog} onClose={() => setOpenSaveAsDialog(false)}>
                <DialogTitle>{t('Thematic.FamilyTree.SaveAsNew')}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={t('Thematic.FamilyTree.Name')}
                        value={saveAsTitle}
                        onChange={(e) => setSaveAsTitle(e.target.value)}
                        margin="dense"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSaveAsDialog(false)}>{t('Common.Cancel')}</Button>
                    <Button 
                        onClick={() => performSaveToDatabase(true, saveAsTitle)} 
                        variant="contained" 
                        disabled={!saveAsTitle.trim()}
                    >
                        {t('Thematic.FamilyTree.Save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
